/* eslint-disable prettier/prettier */
import { Activity } from '../types/schedule.types';
import { apiService } from './api.service';
import { getMonthRange, toDateOnly } from '../utils/dateUtils';

export interface FetchActivitiesParams {
	start: Date;
	end: Date;
}

interface ScheduleResponseData {
	items: Activity[];
	total: number;
	summary: Record<string, number>;
}

export interface LessonParticipant {
	participantid: string;
	userid: string;
	fullname: string;
	email: string;
	profilepicture?: string | null;
	designation?: string | null;
	attendancestatus: 'attended' | 'absent' | 'excused' | 'late' | null;
	attendancemarkedat?: string | null;
	attendancemarkedby?: string | null;
	joinedat?: string | null;
	leftat?: string | null;
	durationminutes?: number | null;
	notes?: string | null;
	enrollmentid: string;
	enrollmentstatus: string;
	enrollmentprogress?: number;
}

export interface LessonDetail {
	id: string;
	title: string;
	description?: string | null;
	contenttype: string;
	sessiontype: string;
	scheduledstarttime: string;
	scheduledendtime: string;
	timezone: string;
	duration?: number | null;
	meetingurl?: string | null;
	recordingurl?: string | null;
	attendancerequired: boolean;
	attendancegraceperiodminutes: number;
	allowlateattendance: boolean;
	module: {
		id: string;
		title: string;
		moduletype: string;
	};
	course: {
		id: string;
		title: string;
		description?: string | null;
		thumbnail?: string | null;
	};
	activitytype?: {
		id: string;
		title: string;
		color: string;
		icon?: string | null;
	} | null;
	deliveredby?: {
		id: string;
		fullname: string;
		email: string;
		profilepicture?: string | null;
	} | null;
	participants: LessonParticipant[];
	participantcount: number;
}

export interface MarkAttendanceParticipant {
	userid: string;
	attendancestatus: 'attended' | 'absent' | 'excused' | 'late';
	joinedat?: string;
	leftat?: string;
	notes?: string;
}

export async function fetchActivities(
	params: FetchActivitiesParams
): Promise<Activity[]> {
	const { start, end } = params;

	// Guard against an invalid/empty range producing "Invalid Date" in the
	// query string (which the backend would parse as NaN and silently
	// return nothing).
	if (
		!(start instanceof Date) ||
		!(end instanceof Date) ||
		isNaN(start.getTime()) ||
		isNaN(end.getTime())
	) {
		throw new Error('fetchActivities: invalid date range');
	}

	// `start` is local 00:00:00.000 and `end` is local 23:59:59.999 (set by
	// getWeekStart/getWeekEnd). toISOString() converts those exact instants
	// to UTC, which is what the backend compares scheduledstarttime against
	// via `new Date(filters.to)`. This is correct in every timezone — e.g.
	// in IST a June grid sends from=2026-05-31T18:30:00Z (= Jun 1 00:00 IST)
	// to=2026-07-05T18:29:59.999Z (= Jul 5 23:59:59 IST). Do NOT switch to
	// a date-only string: `new Date('2026-07-05')` is UTC midnight, which
	// would drop every activity later that final day.
	const fromIso = start.toISOString();
	const toIso = end.toISOString();

	// Send BOTH naming conventions. The frontend's correct names are
	// `from`/`to` (what scheduleService.getUserSchedule reads), but if the
	// deployed backend still reads `startDate`/`endDate`, those were being
	// ignored — so it fell back to its default ~30-day window and the month
	// fetch looked like it only returned a week's worth. Sending both makes
	// the range authoritative regardless of which the server honours.
	const query = {
		from: fromIso,
		to: toIso,
		startDate: fromIso,
		endDate: toIso,
	};

	if (process.env.NODE_ENV !== 'production') {
		// eslint-disable-next-line no-console
		console.debug(
			'[fetchActivities] range %s → %s (%d days)',
			fromIso,
			toIso,
			Math.round((end.getTime() - start.getTime()) / 86400000) + 1
		);
	}

	const response = await apiService.get<ScheduleResponseData>(
		`/schedule/activities`,
		query
	);

	if (!response.success) {
		throw new Error(response.message || 'Activities not found');
	}

	const items = response.data?.items ?? [];

	const seen = new Set<string>();
	return items.filter(item => {
		if (seen.has(item.id)) return false;
		seen.add(item.id);
		return true;
	});
}

export const fetchWeekActivities = fetchActivities;

export async function fetchEventDots(
	year: number,
	month: number
): Promise<string[]> {
	try {
		const range = getMonthRange(year, month);
		const items = await fetchActivities({
			start: range.start,
			end: range.end,
		});

		const keys = new Set<string>();
		for (const item of items) {
			if (item.startTime) keys.add(toDateOnly(item.startTime));
			else if (item.date) keys.add(item.date);
		}
		return Array.from(keys);
	} catch (err) {
		console.error('fetchEventDots error:', err);
		return [];
	}
}

export async function fetchLessonDetail(
	lessonId: string
): Promise<LessonDetail | null> {
	try {
		const response = await apiService.get<LessonDetail>(
			`/schedule/activities/${lessonId}`
		);

		if (!response.success) {
			throw new Error(response.message || 'Lesson detail not found');
		}

		return response.data;
	} catch (err) {
		console.error('fetchLessonDetail error:', err);
		return null;
	}
}

export async function markLessonAttendance(
	lessonId: string,
	participants: MarkAttendanceParticipant[]
) {
	try {
		const response = await apiService.post(
			`/schedule/activities/${lessonId}/attendance`,
			{
				participants,
			}
		);

		if (!response.success) {
			throw new Error(response.message || 'Failed to mark attendance');
		}

		return response.data;
	} catch (err) {
		console.error('markLessonAttendance error:', err);
		throw err;
	}
}
